import React, { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { User, Building2, CheckCircle, X, Calendar, Phone, MessageCircle, ClipboardList } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { safeArray, safeString } from '@/components/safeRender';
import { formatCurrency } from '@/lib/formatters';
import { effectiveTituloStatus } from '@/lib/utils';
import { openClienteWhatsApp } from '@/utils/clienteWhatsApp';
import { toast } from 'react-hot-toast';
import {
  cardSectionBorder,
  gestorBadgeAccepted,
  gestorBadgeDenied,
  operatorAvatar,
  operatorAvatarEmpty,
  operatorAvatarIcon,
  statBoxBlue,
  statBoxBlueCaption,
  statBoxBlueLabel,
  statBoxOrange,
  statBoxOrangeLabel,
  statBoxOrangeValue,
  statBoxRed,
  statBoxRedCaption,
  statBoxRedLabel,
  statBoxTotal,
} from '@/lib/uiClasses';
import {
  calcularDiasAtraso,
  getClientePhoneFromCard,
  getCompanyNameFromCard,
  getGestorActionStatus,
  getOperadoresFromTitulos,
  getStatusLocacaoFromTitulos,
  getValorVencidoFromTitulos,
} from './kanbanCardUtils';

export interface KanbanSortableCardProps {
  card: any;
  isSelected?: boolean;
  onSelect?: (cardId: string, selected: boolean) => void;
  onCardClick?: (card: any) => void;
  /** Abre o modal já na aba de contato/feedback */
  onQuickContact?: (card: any) => void;
}

const SortableCardInner: React.FC<KanbanSortableCardProps> = ({
  card,
  isSelected = false,
  onSelect,
  onCardClick,
  onQuickContact,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const cliente = card.cliente;
  const titulos = safeArray(card.titulos) as any[];
  const gestorStatus = getGestorActionStatus(card);
  const phone = getClientePhoneFromCard(card);

  const statusLocacao = getStatusLocacaoFromTitulos(titulos);
  const companyName = getCompanyNameFromCard(card);
  const valorTotal = titulos.reduce((sum: number, t: any) => sum + Number(t.saldo || t.valor), 0);
  const valorVencidoTotal = getValorVencidoFromTitulos(titulos);
  const diasAtraso = calcularDiasAtraso(titulos);
  const operadoresAlocados = getOperadoresFromTitulos(titulos);
  const titulosVencidos = titulos.filter(
    (t: any) => effectiveTituloStatus(t.status, t.vencimento) === 'VENCIDO' || t.status === 'PARCIAL'
  );

  const stopDrag = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!phone) {
      toast.error('Cliente sem telefone cadastrado');
      return;
    }
    window.open(`tel:${phone.replace(/\D/g, '')}`, '_self');
  };

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!phone) {
      toast.error('Cliente sem WhatsApp/telefone cadastrado');
      return;
    }
    const nome = safeString(cliente?.nome, 'cliente');
    openClienteWhatsApp(phone, `Olá ${nome}, tudo bem?`);
  };

  const handleContact = (e: React.MouseEvent) => {
    e.stopPropagation();
    (onQuickContact ?? onCardClick)?.(card);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
          bg-card text-card-foreground rounded-lg border shadow-sm
          hover:shadow-md dark:hover:shadow-lg dark:hover:shadow-slate-900/50
          transition-all duration-200
          cursor-grab active:cursor-grabbing
          ${isSelected ? 'ring-2 ring-green-500 border-green-500' : ''}
          ${gestorStatus === 'ACEITO' ? 'border-green-400 dark:border-green-500 hover:border-green-500 dark:hover:border-green-400' : ''}
          ${gestorStatus === 'NEGADO' ? 'border-red-400 dark:border-red-500 hover:border-red-500 dark:hover:border-red-400' : ''}
          ${!gestorStatus && !isSelected ? 'border-border hover:border-muted-foreground/30' : ''}
          ${isDragging ? 'shadow-lg dark:shadow-xl dark:shadow-slate-900/70 rotate-2 scale-105' : ''}
        `}
      onClick={() => onCardClick?.(card)}
    >
      <div className={`p-3 ${cardSectionBorder}`}>
        {(gestorStatus === 'ACEITO' || gestorStatus === 'NEGADO') && (
          <div className="mb-2 flex justify-center">
            {gestorStatus === 'ACEITO' && (
              <Badge className={gestorBadgeAccepted}>
                <CheckCircle className="w-3 h-3 mr-1" />
                Aceito pelo Gestor
              </Badge>
            )}
            {gestorStatus === 'NEGADO' && (
              <Badge className={gestorBadgeDenied}>
                <X className="w-3 h-3 mr-1" />
                Negado pelo Gestor
              </Badge>
            )}
          </div>
        )}

        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {onSelect && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(card.id, !isSelected);
                }}
                className="cursor-pointer"
              >
                <Checkbox checked={isSelected} onChange={() => {}} />
              </div>
            )}
            <div className={operatorAvatar}>
              <User className={operatorAvatarIcon} />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-foreground truncate">
                {safeString(cliente?.nome, 'Cliente sem nome')}
              </h4>
              {cliente?.codigo && (
                <p className="text-xs text-muted-foreground font-mono">{cliente.codigo}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Badge variant="outline" className="text-xs">
              {titulos.length}
            </Badge>
          </div>
        </div>

        {operadoresAlocados.length > 0 ? (
          <div className="mt-2 flex items-center space-x-2">
            <div className={operatorAvatar}>
              <User className={operatorAvatarIcon} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">
                Operador: {operadoresAlocados[0].name}
              </p>
              {operadoresAlocados.length > 1 && (
                <p className="text-xs text-muted-foreground/80">
                  +{operadoresAlocados.length - 1} outros
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-2 flex items-center space-x-2">
            <div className={operatorAvatarEmpty}>
              <User className="w-3 h-3 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Sem operador alocado</p>
            </div>
          </div>
        )}

        {(companyName || statusLocacao) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {companyName && (
              <Badge className="text-xs bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600">
                <Building2 className="w-3 h-3 mr-1" />
                <span className="truncate max-w-[180px]">{companyName}</span>
              </Badge>
            )}
            {statusLocacao && (
              <Badge
                className={`
                    text-xs
                    ${statusLocacao === 'ATIVA' ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200' : ''}
                    ${statusLocacao === 'FINALIZADA' ? 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200' : ''}
                    ${statusLocacao === 'CANCELADA' ? 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200' : ''}
                    ${!['ATIVA', 'FINALIZADA', 'CANCELADA'].includes(statusLocacao) ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-200' : ''}
                  `}
              >
                {statusLocacao}
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className={statBoxBlue}>
            <div className={statBoxBlueLabel}>{titulosVencidos.length}</div>
            <div className={statBoxBlueCaption}>Em Atraso</div>
          </div>
          <div className={statBoxRed}>
            <div className={statBoxRedLabel}>{formatCurrency(valorVencidoTotal)}</div>
            <div className={statBoxRedCaption}>Valor Vencido</div>
          </div>
        </div>

        {diasAtraso > 0 && (
          <div className={statBoxOrange}>
            <div className="flex items-center space-x-2">
              <Calendar className="w-3 h-3 text-orange-600 dark:text-orange-400" />
              <span className={statBoxOrangeLabel}>Dias em Atraso</span>
            </div>
            <span className={statBoxOrangeValue}>
              {diasAtraso} dia{diasAtraso !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        <div className={statBoxTotal}>
          <div className="text-sm font-bold text-foreground">{formatCurrency(valorTotal)}</div>
          <div className="text-xs text-muted-foreground">Total Geral</div>
        </div>

        <div
          className="flex items-center justify-center gap-1 pt-1 border-t border-border/60"
          onPointerDown={stopDrag}
          onClick={stopDrag}
        >
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            title={phone ? 'Ligar' : 'Sem telefone'}
            aria-label="Ligar para o cliente"
            onClick={handleCall}
            disabled={!phone}
          >
            <Phone className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            title={phone ? 'WhatsApp' : 'Sem WhatsApp'}
            aria-label="Abrir WhatsApp"
            onClick={handleWhatsApp}
            disabled={!phone}
          >
            <MessageCircle className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            title="Registrar contato"
            aria-label="Registrar contato"
            onClick={handleContact}
          >
            <ClipboardList className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

function cardsEqual(prev: KanbanSortableCardProps, next: KanbanSortableCardProps): boolean {
  if (
    prev.card.id !== next.card.id ||
    prev.isSelected !== next.isSelected ||
    prev.card.gestorActionStatus !== next.card.gestorActionStatus ||
    prev.card.proximoContatoAt !== next.card.proximoContatoAt ||
    prev.card.ultimoContatoAt !== next.card.ultimoContatoAt ||
    prev.onSelect !== next.onSelect ||
    prev.onCardClick !== next.onCardClick ||
    prev.onQuickContact !== next.onQuickContact
  ) {
    return false;
  }
  const titulosPrev = prev.card.titulos || [];
  const titulosNext = next.card.titulos || [];
  if (titulosPrev.length !== titulosNext.length) return false;
  if (prev.card.valorTotal !== next.card.valorTotal) return false;
  if (prev.card.valorTotalVencido !== next.card.valorTotalVencido) return false;
  const opsPrev = titulosPrev.map((t: any) => t.operadorId || t.operador?.id).join(',');
  const opsNext = titulosNext.map((t: any) => t.operadorId || t.operador?.id).join(',');
  return opsPrev === opsNext;
}

export const SortableCard = memo(SortableCardInner, cardsEqual);
